/**
 * getBookingContextMessage(booking, { hoursLeft })
 * -------------------------------------------------
 * Retourne un message d'aide contextuel { tone, icon, text } ou null
 * selon l'état réel de la réservation. Utilisé pour afficher une ligne
 * discrète sous chaque booking, uniquement quand c'est utile.
 *
 * Tones : warning (amber), info (violet), success (emerald), neutral (slate),
 *         danger (red), gold.
 *
 * Règles (uniquement cas "utiles", pas de pollution visuelle) :
 *   - Report demandé (coach ou user)   → violet : "Votre coach propose… — répondez avant la séance"
 *   - Confirmée > 24 h                 → gold  : "Annulation gratuite jusqu'au <date>"
 *   - Confirmée < 24 h                 → amber : "Plus d'annulation possible — commence dans < 24 h"
 *   - pending_payment                  → gold  : "Validez le paiement pour sécuriser le créneau"
 *   - expired                          → slate : "Créneau libéré automatiquement faute de paiement"
 *   - cancelled + refund_status failed → amber : "Remboursement en cours — notre équipe vous recontacte"
 *   - cancelled + refunded (<48h)      → emerald: "Remboursement déclenché — délai bancaire 5–10 jours"
 *   - completed + no ai_report + has_recording → gold: "Rapport IA en attente de génération"
 */
import {
  AlertTriangle, Hourglass, Ban, Sparkles, CheckCircle2,
  FileText, Clock, ShieldCheck,
} from 'lucide-react';
import { formatDateLongFR } from './formatFR';

function hoursUntil(dateStr, startTime) {
  try {
    const t = new Date(`${dateStr}T${startTime}:00`);
    return (t.getTime() - Date.now()) / 3600000;
  } catch { return 0; }
}

/** Returns the date/time at which the 24h cancellation window closes. */
function refundDeadlineLabel(dateStr, startTime) {
  try {
    const target = new Date(`${dateStr}T${startTime}:00`);
    target.setHours(target.getHours() - 24);
    const d = target.toISOString().slice(0, 10);
    const time = target.toTimeString().slice(0, 5);
    return `${formatDateLongFR(d)} à ${time}`;
  } catch { return null; }
}

export function getBookingContextMessage(b) {
  if (!b) return null;
  const { status, refund_status, reschedule_request, has_recording, ai_report } = b;
  const hoursLeft = hoursUntil(b.date, b.start_time);

  // Report demandé (priorité)
  if (status === 'confirmed' && reschedule_request?.status === 'pending') {
    const isFromCoach = reschedule_request.proposed_by === 'coach';
    return {
      tone: 'info',
      icon: Sparkles,
      text: isFromCoach
        ? 'Votre coach propose un nouveau créneau — répondez avant la séance pour qu\'il soit validé.'
        : 'Votre demande de report est en attente de confirmation.',
    };
  }

  // Confirmée
  if (status === 'confirmed' && hoursLeft > 0) {
    if (hoursLeft <= 24) {
      return {
        tone: 'warning',
        icon: AlertTriangle,
        text: 'Plus d\'annulation possible — la séance commence dans moins de 24 h.',
      };
    }
    const deadline = refundDeadlineLabel(b.date, b.start_time);
    return {
      tone: 'gold',
      icon: ShieldCheck,
      text: deadline
        ? `Annulation gratuite jusqu'au ${deadline} (remboursement intégral).`
        : 'Annulation gratuite jusqu\'à 24 h avant la séance.',
    };
  }

  // Paiement en attente
  if (status === 'pending_payment') {
    return {
      tone: 'gold',
      icon: Hourglass,
      text: 'Validez le paiement pour sécuriser ce créneau (libéré automatiquement après 30 min sans paiement).',
    };
  }

  // Expiré : pas de ligne contextuelle (info déjà dans le tooltip du badge)
  // Cas volontairement retiré pour ne pas polluer le listing (Feb 2026).

  // Annulée + refund failed
  if (status === 'cancelled' && refund_status === 'failed') {
    return {
      tone: 'warning',
      icon: Clock,
      text: 'Remboursement en cours de traitement — notre équipe vous recontacte sous 24 h si besoin.',
    };
  }

  // Annulée + refunded
  if (status === 'cancelled' && refund_status === 'refunded' && b.refund_amount > 0) {
    return {
      tone: 'success',
      icon: CheckCircle2,
      text: `${b.refund_amount}€ remboursés — délai bancaire habituel 5 à 10 jours ouvrés.`,
    };
  }

  // Terminée sans rapport IA mais avec recording
  if (status === 'completed' && has_recording && !ai_report) {
    return {
      tone: 'info',
      icon: FileText,
      text: 'Rapport IA disponible — cliquez sur « Générer le rapport » dans le menu Actions.',
    };
  }

  return null;
}

/* ─── Styles harmonisés par tone ─── */
export const CONTEXT_TONES = {
  warning: 'text-[#B45309] bg-amber-50 border-amber-200',
  info: 'text-[#5B21B6] bg-[#F5F3FF] border-violet-200',
  success: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  neutral: 'text-[#64748B] bg-slate-50 border-slate-200',
  danger: 'text-red-700 bg-red-50 border-red-200',
  gold: 'text-[#92710A] bg-[#FEF7E0] border-[#F3D060]',
};
